import { Crown, Pencil, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';
import { Badge } from '@ncoder/ui';
import { IconActionButton } from '@/components/molecules/IconActionButton';
import type { OrgRole } from '@/services/org-admin';
import { NO_PERMISSION_REASON } from './types';

export interface RoleRowProps {
  role: OrgRole;
  /**
   * How many members currently hold this role.
   *
   * Shown because it answers the two questions the card previously left unanswerable: is this role
   * actually in use, and why can't I delete it? The API refuses to delete an assigned role, and the
   * old card gave no hint of that until the request came back with an error.
   */
  memberCount: number;
  canManage: boolean;
  onEdit: (role: OrgRole) => void;
  onDelete: (role: OrgRole) => void;
  /** Opens the member picker for this role. */
  onAssignMembers: (role: OrgRole) => void;
  /**
   * Why assignment is unavailable, or `undefined` when it is. Computed by the panel because it
   * depends on the actor's authority and the roster, neither of which this card knows.
   */
  assignBlockedReason?: string;
  isDeleting: boolean;
}

const LEVEL_ICON = { owner: Crown, admin: ShieldCheck } as const;

/**
 * Assigning members from a role card is deferred — flip to re-enable.
 *
 * A flag rather than commented-out JSX, matching `SHOW_EDIT_BUDGET` in `MemberUsageTable`. Commented
 * markup stops type-checking, so its props and imports read as unused and fail the build under
 * `noUnusedLocals` — which is what happened here. Behind a flag the code stays compiled and
 * type-safe, so re-enabling is one word rather than an archaeology exercise.
 *
 * TO RE-ENABLE: flip this to `true`, and restore the screenshot coverage in `qa/orgAdmin.mjs` by
 * adding back the two `dialog: "assign-members"` jobs plus their driver:
 *
 *     if (dialog === "assign-members") {
 *       await page.getByRole("button", { name: /^Assign members to Analyst/ }).first().click();
 *       await page.waitForTimeout(400);
 *     }
 *
 * Deliberately WITHOUT a `.catch()`, unlike the older hover-revealed dialog drivers: these actions
 * are persistent, so a missing button is a broken card and should fail the run.
 *
 * The behavioural checks in `qa/roles.mjs` DO depend on this flag — they open the dialog through this
 * card — so they are skipped behind `ASSIGN_FROM_CARD_ENABLED` there and must be re-enabled at the
 * same time. The one check that survives the deferral is the identifier assertion driven through the
 * Members tab dropdown, which is the most valuable one in the file.
 */
const SHOW_ASSIGN_MEMBERS = false;

export function RoleRow({
  role,
  memberCount,
  canManage,
  onEdit,
  onDelete,
  onAssignMembers,
  assignBlockedReason,
  isDeleting,
}: RoleRowProps) {
  const LevelIcon = role.management_level !== 'member' ? LEVEL_ICON[role.management_level] : null;

  const deleteBlockedReason = !canManage
    ? NO_PERMISSION_REASON
    : role.is_default
      ? "The org's default role cannot be deleted"
      : memberCount > 0
        ? `${memberCount} member${memberCount === 1 ? '' : 's'} still hold this role — reassign them first`
        : undefined;

  return (
    <div className="oa-raised oa-raised-interactive group flex h-full flex-col p-3.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="oa-title truncate">{role.name}</h3>
          <p className="oa-meta mt-1 flex items-center gap-1.5">
            <Users className="h-3 w-3 shrink-0" aria-hidden />
            {memberCount === 0 ? 'Unassigned' : `${memberCount} member${memberCount === 1 ? '' : 's'}`}
          </p>
        </div>
        {/*
          PERSISTENT, not hover-revealed — and this is a reversal worth recording.

          These actions used to carry `oa-row-actions`, which reveals on hover, on focus-within and
          always on touch, on the reasoning that a grid of roles should read as a set of roles rather
          than a grid of icon buttons. That reasoning holds for a table ROW, where the row itself is
          the thing you point at and the action appears where your cursor already is. It does not hold
          for a card grid: editing a role is the main thing this screen is for, and an affordance that
          only exists once you happen to hover the right card is one a reader can conclude is absent
          entirely. It was.

          The member and invitation rows keep the hover treatment, so this deliberately does not
          change `.oa-row-actions` itself — only this card's use of it.
        */}
        <div className="flex shrink-0 items-center gap-0.5">
          {SHOW_ASSIGN_MEMBERS && (
            <IconActionButton
              label={`Assign members to ${role.name}`}
              icon={UserPlus}
              size="sm"
              disabled={!canManage || !!assignBlockedReason}
              disabledReason={canManage ? assignBlockedReason : NO_PERMISSION_REASON}
              onClick={() => onAssignMembers(role)}
            />
          )}
          <IconActionButton
            label={`Edit ${role.name}`}
            icon={Pencil}
            size="sm"
            disabled={!canManage}
            disabledReason={canManage ? undefined : NO_PERMISSION_REASON}
            onClick={() => onEdit(role)}
          />
          <IconActionButton
            label={`Delete ${role.name}`}
            icon={Trash2}
            tone="destructive"
            size="sm"
            isPending={isDeleting}
            disabled={!!deleteBlockedReason || isDeleting}
            disabledReason={deleteBlockedReason}
            onClick={() => onDelete(role)}
          />
        </div>
      </div>

      {(role.is_default || LevelIcon) && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {LevelIcon && (
            <Badge
              variant="outline"
              className="gap-1 border-primary/25 bg-primary/10 py-0 text-[11px] font-medium capitalize text-primary"
              title="Management level — the admin authority this role carries, in addition to the modules it grants."
            >
              <LevelIcon className="h-3 w-3" aria-hidden />
              {role.management_level}
            </Badge>
          )}
          {role.is_default && (
            <Badge
              variant="outline"
              className="border-border/70 py-0 text-[11px] font-medium text-muted-foreground"
              title="Created automatically when this org was set up — every org has one. It is not auto-assigned to new members; assign it like any other role."
            >
              Default
            </Badge>
          )}
        </div>
      )}

      {/* The access list is the substance of a role, so it gets the room. It used to sit in its own
          bordered, tinted sub-box — a third border level inside an already-nested card. A label and
          a hairline separate it now. */}
      <div className="mt-3 flex flex-1 flex-col gap-1.5 border-t border-border/50 pt-2.5">
        <span className="oa-micro">Grants access to</span>
        <div className="flex flex-wrap content-start gap-1.5">
          {role.capabilities.length === 0 ? (
            <span className="oa-meta">No modules granted</span>
          ) : (
            role.capabilities.map((capability) => (
              <Badge
                key={capability.key}
                variant="outline"
                className="border-border/70 py-0 text-[11px] font-normal text-foreground"
              >
                {capability.label}
              </Badge>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
